/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Me · Tasks — combined delegation tasks + checklist items
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ClipboardList, ClipboardCheck, Calendar, ArrowRight,
  Check, X, AlertCircle, Search,
} from "lucide-react";

const PRIORITY_TONE = {
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-slate-100 text-slate-700",
};

function fmtDue(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch { return iso; }
}

export default function MeTasks() {
  const [tab, setTab] = useState("open");  // open | done
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/delegation/tasks", {
        params: { mode: "assignee", limit: 100 },
      });
      setTasks(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = tasks;
    if (tab === "open") arr = arr.filter((t) =>
      !["done", "completed", "verified", "cancelled", "rejected"].includes((t.status || "").toLowerCase()));
    else arr = arr.filter((t) =>
      ["done", "completed", "verified"].includes((t.status || "").toLowerCase()));
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter((t) =>
        (t.title || "").toLowerCase().includes(q) ||
        (t.description || "").toLowerCase().includes(q));
    }
    return arr;
  }, [tasks, tab, search]);

  const counts = useMemo(() => ({
    open: tasks.filter((t) => !["done", "completed", "verified", "cancelled", "rejected"]
      .includes((t.status || "").toLowerCase())).length,
    done: tasks.filter((t) => ["done", "completed", "verified"]
      .includes((t.status || "").toLowerCase())).length,
  }), [tasks]);

  const markDone = async (id) => {
    setBusyId(id);
    try {
      // Backend enum: ['acknowledged','cancelled','completed','extended',
      // 'in_progress','pending','task_over'] — use 'completed' for done.
      await api.post(`/delegation/tasks/${id}/status`, { status: "completed" });
      toast.success("Marked done");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3" data-testid="me-tasks">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">My Tasks</div>
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Tasks &amp; Checklists</h1>
        <p className="text-[11px] text-slate-500">Items assigned to you · {counts.open} open</p>
      </div>

      {/* Checklist quick link */}
      <Link to="/checklist"
        data-testid="me-checklist-link"
        className="bg-white rounded-xl ring-1 ring-red-200 p-3 flex items-center gap-3 hover:bg-red-50 transition">
        <div className="h-10 w-10 rounded-lg bg-red-100 brand-text flex items-center justify-center">
          <ClipboardCheck className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-slate-900">My Daily Checklist</div>
          <div className="text-[11px] text-slate-500">SOP-driven daily activity log</div>
        </div>
        <ArrowRight className="h-4 w-4 text-slate-400" />
      </Link>

      {/* Search */}
      <div className="relative">
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tasks…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg ring-1 ring-slate-200 bg-white"
          data-testid="me-tasks-search"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5">
        {[
          { k: "open", label: "Open" },
          { k: "done", label: "Done" },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`me-tasks-tab-${t.k}`}
            className={`flex-1 py-2 rounded-lg text-xs font-semibold ring-1 transition ${
              tab === t.k
                ? "bg-slate-900 text-white ring-slate-900"
                : "bg-white text-slate-700 ring-slate-200"
            }`}
          >
            {t.label} · {counts[t.k]}
          </button>
        ))}
      </div>

      {/* Task list */}
      {loading ? (
        <div className="text-center text-xs text-slate-400 py-8">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-xs text-slate-400 py-8 bg-white rounded-xl ring-1 ring-slate-200">
          {tab === "open" ? "🎉 All caught up — no open tasks!" : "No completed tasks yet."}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <TaskCard key={t.id} task={t} busy={busyId === t.id}
                      onDone={() => markDone(t.id)} canMarkDone={tab === "open"} />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskCard({ task, busy, onDone, canMarkDone }) {
  const t = task;
  const isOverdue = t.due_date && new Date(t.due_date) < new Date(new Date().toDateString());
  const priority = (t.priority || "medium").toLowerCase();
  return (
    <Link
      to={`/delegation/tasks/${t.id}`}
      data-testid={`me-task-card-${t.id}`}
      className="block bg-white rounded-xl ring-1 ring-slate-200 p-3 hover:ring-slate-300 transition"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold uppercase ${PRIORITY_TONE[priority] || PRIORITY_TONE.low}`}>
              {priority}
            </span>
            {isOverdue && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-600 text-white font-semibold inline-flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Overdue
              </span>
            )}
            {t.due_date && (
              <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
                <Calendar className="h-3 w-3" /> {fmtDue(t.due_date)}
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-slate-900 leading-snug">{t.title}</div>
          {t.description && (
            <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
          )}
          <div className="text-[10px] text-slate-500 mt-1">
            From <strong>{t.assigned_by_name || "—"}</strong>
            {t.task_type && <span> · {t.task_type}</span>}
          </div>
        </div>
        <ClipboardList className="h-5 w-5 text-slate-300 flex-shrink-0" />
      </div>
      {canMarkDone && (
        <div className="mt-2 grid grid-cols-1">
          <button
            onClick={(e) => { e.preventDefault(); onDone(); }}
            disabled={busy}
            data-testid={`me-task-done-${t.id}`}
            className="rounded-lg bg-emerald-600 text-white py-2 text-xs font-bold disabled:bg-slate-300 flex items-center justify-center gap-1.5"
          >
            <Check className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Mark Done"}
          </button>
        </div>
      )}
    </Link>
  );
}
